import { useState, useEffect } from 'react';
import { X, Save, AlertTriangle } from 'lucide-react';
import { topPicksPoolApi } from '../../services/adminApi';
import BookSearchSelect from './BookSearchSelect';

const LEGACY_GENRE_OPTIONS = [
  'animals',
  'adventure',
  'mystery',
  'poetry',
  'classics',
  'fantasy',
  'sci-fi',
  'funny',
  'history',
  'non-fiction'
];

const TopPicksRecordModal = ({ isOpen, onClose, record = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    book_id: '',
    primary_genre: '',
    secondary_genres: [],
    rank: 1,
    note: '',
    status: 'active'
  });
  const [signalPreview, setSignalPreview] = useState(null);
  const [signalPreviewLoading, setSignalPreviewLoading] = useState(false);
  const [signalPreviewError, setSignalPreviewError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasExistingStoredSignals = Boolean(record?.primary_genre);

  useEffect(() => {
    if (isOpen) {
      if (record) {
        setFormData({
          book_id: record.book_id || '',
          primary_genre: record.primary_genre || '',
          secondary_genres: record.secondary_genres || [],
          rank: record.rank || 1,
          note: record.note || '',
          status: record.status || 'active'
        });
      } else {
        setFormData({
          book_id: '',
          primary_genre: '',
          secondary_genres: [],
          rank: 1,
          note: '',
          status: 'active'
        });
      }
      setError(null);
      setSignalPreview(null);
      setSignalPreviewError(null);
    }
  }, [isOpen, record]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const bookId = String(formData.book_id || '').trim();
    if (!bookId) {
      setSignalPreview(null);
      setSignalPreviewError(null);
      setSignalPreviewLoading(false);
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      try {
        setSignalPreviewLoading(true);
        setSignalPreviewError(null);
        const result = await topPicksPoolApi.previewTopPicksSignals(bookId);
        setSignalPreview(result.signals || null);
      } catch (err) {
        setSignalPreview(null);
        setSignalPreviewError(err.message || 'Failed to preview Top Picks signals');
      } finally {
        setSignalPreviewLoading(false);
      }
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [formData.book_id, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSecondaryGenreChange = (genre) => {
    setFormData(prev => {
      const newGenres = prev.secondary_genres.includes(genre)
        ? prev.secondary_genres.filter(g => g !== genre)
        : [...prev.secondary_genres, genre];
      return { ...prev, secondary_genres: newGenres };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.book_id) {
      setError('Please search for and select a book.');
      return;
    }
    if (!formData.rank || formData.rank < 1) {
      setError('Rank must be a positive number.');
      return;
    }

    const requiresManualFallback = signalPreview?.requires_manual_fallback;
    if (requiresManualFallback && !formData.primary_genre) {
      setError('This book does not have usable canonical signals yet. Select a legacy primary genre fallback.');
      return;
    }

    try {
      setLoading(true);
      const apiData = {
        book_id: formData.book_id,
        rank: parseInt(formData.rank),
        note: formData.note.trim() || null,
        status: formData.status
      };

      if (requiresManualFallback) {
        apiData.primary_genre = formData.primary_genre;
        apiData.secondary_genres = formData.secondary_genres.length > 0 ? formData.secondary_genres : null;
      }

      if (record) {
        await topPicksPoolApi.updateTopPicksPoolRecord(record.id, apiData);
      } else {
        await topPicksPoolApi.createTopPicksPoolRecord(apiData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      let errorMessage = err.message || 'Failed to save record';
      if (errorMessage.includes('already exists')) {
        errorMessage = 'This book is already in the Top Picks Pool for the same genre.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-text-primary">
            {record ? 'Edit Top Picks Record' : 'Add Book to Top Picks Pool'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <span className="text-red-800">{error}</span>
              </div>
            </div>
          )}

          <BookSearchSelect
            label="Book"
            required
            selectedId={formData.book_id}
            selectedLabel={record?.title || ''}
            onSelect={(book) => handleInputChange('book_id', book ? book.id : '')}
            helperText="Which children see this book is decided by the book's own reading age."
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Rank *</label>
              <input
                type="number"
                min="1"
                value={formData.rank}
                onChange={(e) => handleInputChange('rank', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Status</label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="active">Active</option>
                <option value="passive">Passive</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
              <p className="text-sm font-medium text-text-primary mb-1">Top Picks signals</p>
              {!formData.book_id && (
                <p className="text-sm text-text-secondary">
                  Select a book to preview system-derived Top Picks signals.
                </p>
              )}
              {formData.book_id && signalPreviewLoading && (
                <p className="text-sm text-text-secondary">Checking canonical book metadata...</p>
              )}
              {signalPreviewError && (
                <p className="text-sm text-red-700">{signalPreviewError}</p>
              )}
              {signalPreview && !signalPreview.requires_manual_fallback && (
                <>
                  <p className="text-sm text-green-800">
                    System-derived signals will be used for this book.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full bg-green-100 px-2 py-1 text-green-800">
                      Primary: {signalPreview.primary_genre}
                    </span>
                    {signalPreview.secondary_genres?.map((genre) => (
                      <span key={genre} className="rounded-full bg-green-50 px-2 py-1 text-green-700">
                        {genre}
                      </span>
                    ))}
                  </div>
                </>
              )}
              {signalPreview?.requires_manual_fallback && (
                <p className="text-sm text-amber-800">
                  {hasExistingStoredSignals
                    ? 'Canonical book metadata is not strong enough to replace this row yet. Existing stored signals are kept unless you change the legacy fallback fields below.'
                    : 'Canonical book metadata is not strong enough for this book yet. Use the legacy genre fallback below.'}
                </p>
              )}
            </div>

            {signalPreview?.requires_manual_fallback && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">Legacy Primary Genre *</label>
                  <select
                    value={formData.primary_genre}
                    onChange={(e) => handleInputChange('primary_genre', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
                    required
                  >
                    <option value="">Select a fallback genre</option>
                    {LEGACY_GENRE_OPTIONS.map((genre) => (
                      <option key={genre} value={genre}>{genre}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Legacy Secondary Genres (Optional)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded p-2 bg-white">
                    {LEGACY_GENRE_OPTIONS.map((genre) => (
                      <label key={genre} className="flex items-center space-x-2 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={formData.secondary_genres.includes(genre)}
                          onChange={() => handleSecondaryGenreChange(genre)}
                          className="rounded border-gray-300"
                        />
                        <span className="text-text-primary">{genre}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Curator Note (Optional)</label>
            <textarea
              value={formData.note}
              onChange={(e) => handleInputChange('note', e.target.value)}
              placeholder="Add a note about why this book was selected for the Top Picks pool..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 border border-gray-200 text-text-secondary rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>{loading ? 'Saving...' : (record ? 'Update Record' : 'Add to Pool')}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TopPicksRecordModal;
